import React, { useEffect, useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from './TypeAhead';
import LookupResultCard from '../pages/order-management/components/LookupResultCard';
import { PermissionAccessProvider } from '../contexts/PermissionAccessContext';
import { searchCarriers, type CarrierRow } from '../services/carriers.service';

/**
 * Reusable carrier picker: search active highway carriers by name and store the
 * carrier's recno (= ordersall.carriercode) while displaying the truckername.
 * Built directly on the shared <TypeAhead> (freeText mode) with an enriched
 * <LookupResultCard> per option — the same async-search pattern as
 * <LookupSearchField>, but rendered INLINE with no top <label> so it drops into
 * the dense Order Info Row (which already has its own "Carrier" label). The
 * stored value differs from the shown text: a pick stores {recno, name}; typing
 * free text unlinks (recno 0) so a partial name never masquerades as a linked
 * carrier.
 *
 * Display-name management: the parent (OrderFormView) persists both the recno
 * (carrierCode) and the name (carrier), so `value.name` is authoritative — we
 * mirror it into local state so the shown name stays stable across the
 * TypeAhead's options churn (its internal input resyncs to `displayValue`).
 *
 * Permission wrapper: TypeAhead disables itself inside a read-only
 * PermissionAccessProvider (accessLevel <= 1) — which is the case on Templates.
 * This field only mounts when the parent already decided it's editable
 * (carrierEnabled), so we wrap in accessLevel={2} to keep the typeahead active.
 */
export interface CarrierValue {
  recno: number; // 0 = unlinked
  name: string;
  scac: string; // carrier's SCAC ('' when unknown / free-typed)
}

interface Props {
  value: CarrierValue;
  onChange: (v: CarrierValue) => void;
  disabled?: boolean;
  /** When provided, an "Add As New Carrier" action shows in the dropdown, called
   *  with the current search term (create mode → opens the New Carrier drawer). */
  onAddNew?: (term: string) => void;
  /** Orange-highlight the input — the stored carrier is inactive or no longer
   *  exists (same styling the plain selects use for an unknown value). */
  invalid?: boolean;
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

export const CarrierLookupField: React.FC<Props> = ({ value, onChange, disabled, onAddNew, invalid }) => {
  const [displayName, setDisplayName] = useState<string>(value.name ?? '');
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [portalEl, setPortalEl] = useState<HTMLElement | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Portal the dropdown into the enclosing <dialog> (top layer) so it isn't
  // clipped when this field renders inside a modal; falls back to undefined
  // (TypeAhead then uses document.body) on the plain /pro page.
  useEffect(() => {
    setPortalEl((wrapRef.current?.closest('dialog') as HTMLElement | null) ?? undefined);
  }, []);

  // Mirror the parent-supplied name (authoritative) into local state so the
  // shown name survives the TypeAhead's option churn and parent re-renders.
  useEffect(() => {
    setDisplayName(value.name ?? '');
  }, [value.name]);

  const runSearch = (term: string) => {
    if (timer.current) clearTimeout(timer.current);
    const q = term.trim();
    if (q.length < MIN_CHARS) {
      setOptions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const rows = await searchCarriers(q);
        setOptions(
          rows.map((r) => ({ value: String(r.recno), label: r.name, data: r })),
        );
      } catch {
        setOptions([]);
      }
    }, DEBOUNCE_MS);
  };

  return (
    <div ref={wrapRef} className="w-full">
      {/* accessLevel={2}: this field only mounts when the parent already
          decided it's editable, so keep the typeahead active even inside a
          read-only (Template) PermissionAccessProvider. */}
      <PermissionAccessProvider accessLevel={2}>
        <TypeAhead
          freeText
          compact
          multiple={false}
          value={displayName}
          options={options}
          // Typing (without picking) unlinks — the user must pick a result to link.
          onChange={(text) => {
            setDisplayName(text);
            onChange({ recno: 0, name: text, scac: '' });
          }}
          onSearch={runSearch}
          onSelect={(opt) => {
            const c = opt.data as CarrierRow;
            setDisplayName(c.name);
            onChange({ recno: c.recno, name: c.name, scac: c.scac ?? '' });
          }}
          renderOption={(opt) => {
            const c = opt.data as CarrierRow;
            return (
              <LookupResultCard
                name={c.name}
                code={c.recno}
                city={c.city}
                state={c.state}
                details={[
                  { label: 'SCAC', value: c.scac },
                  { label: 'MC#', value: c.mcnum },
                  { label: 'DOT#', value: c.dotnum },
                ]}
              />
            );
          }}
          onCreateNew={onAddNew}
          createNewLabel={(term) => `Add As New Carrier${term.trim() ? `: "${term.trim().toUpperCase()}"` : ''}`}
          disabled={disabled}
          placeholder="Search carriers…"
          portalContainer={portalEl}
          className="w-full"
          invalid={invalid}
        />
      </PermissionAccessProvider>
    </div>
  );
};

export default CarrierLookupField;
